import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faServer } from "@fortawesome/free-solid-svg-icons";
import FolderTreeMenu from "./FolderTreeMenu";
import Folder from "./Folder";
import { actions as savedItemsActions } from "../../../actions/savedItems";
import {
  Container,
  MainContent,
  StickyHeader
} from "../../../styled-components/Layout";
import SavedItemExpiration from "../../../constants/savedItemsExpiration";
import SpecialSavedItemFolderIds from "../../../constants/specialSavedItemFolderIds";
import FolderPrompt from "./FolderPrompt";
import {
  isSpecialSavedItemFolder,
  getSpecialFolder
} from "../../../util/savedItemsUtils";
import localize from "../../../util/localizeUtils";

const FolderListContainer = styled(Container)`
  width: 100%;
  flex-grow: 0;
  flex-shrink: 0;
  height: 100%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const FolderContainer = styled(MainContent)`
  height: 100%;
  overflow-y: scroll;
  overflow-x: auto;
  padding: 0.5rem;
  width: auto;
`;

const Tree = styled.ul`
  list-style-type: none;
  margin-bottom: 0.25rem;
  padding: 0;
  &.root {
    margin-left: 0;
    margin-top: 0;
  }
`;

const FolderCategory = styled.div`
  display: flex;
  align-items: center;
`;

const GlobalCategoryIcon = styled.div`
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  margin-right: 0.25rem;
  color: ${props => props.theme.savedItems.globalCategory};
`;

const UserCategoryIcon = styled.div`
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  margin-right: 0.25rem;
  color: ${props => props.theme.savedItems.userCategory};
`;

class FolderTree extends Component {
  constructor(props) {
    super(props);

    this.state = {
      folderPromptVisible: false,
      folderOperation: "add"
    };

    if (props.pullFolders) {
      props.getSavedItemFolders();
    }
  }

  getSelectedFolder = () => {
    const { selectedFolder, userId } = this.props;
    if (isSpecialSavedItemFolder(selectedFolder)) {
      return getSpecialFolder(selectedFolder, userId);
    }
    return this.props.folders[selectedFolder];
  };

  handlePromptSubmit = name => {
    switch (this.state.folderOperation) {
      case "add": {
        const folder = this.getSelectedFolder();
        this.props.addFolder(name, folder);
        break;
      }
      case "delete":
        this.props.removeFolder(this.props.selectedFolder);
        break;
      case "rename":
        this.props.updateFolder(name);
        break;
      default:
        break;
    }
  };

  closeFolderPrompt = () => {
    this.setState({
      folderPromptVisible: false
    });
  };

  handleFolderAction = action => {
    this.setState({
      folderPromptVisible: true,
      folderOperation: action
    });
  };

  renderFolder = (folders, folderId) => {
    // recursively go through and render folders.
    const folder = folders.find(f => f.id === folderId);
    if (folder.subFolders && folder.subFolders.length > 0) {
      return (
        <Folder
          key={folder.id}
          id={folder.id}
          selected={folder.id === this.props.selectedFolder}
          name={folder.name}
          onSelected={this.props.selectFolder}
        >
          {folder.subFolders.map(f => this.renderFolder(folders, f))}
        </Folder>
      );
    }
    return (
      <Folder
        key={folder.id}
        id={folder.id}
        name={folder.name}
        onSelected={this.props.selectFolder}
        selected={folder.id === this.props.selectedFolder}
      />
    );
  };

  render() {
    const { folders, selectedFolder } = this.props;
    const globalFolders = Object.keys(folders)
      .filter(f => !folders[f].owner)
      .map(f => folders[f]);
    const userFolders = Object.keys(folders)
      .filter(f => !!folders[f].owner)
      .map(f => folders[f]);
    const selectedFolderObj =
      folders[Object.keys(folders).find(f => folders[f].id === selectedFolder)];
    return (
      <FolderListContainer>
        <FolderPrompt
          onClose={this.closeFolderPrompt}
          operation={this.state.folderOperation}
          onClick={name => this.handlePromptSubmit(name)}
          visible={this.state.folderPromptVisible}
          folderName={selectedFolderObj ? selectedFolderObj.name : null}
        />
        <StickyHeader>
          <FolderTreeMenu
            add
            remove={!isSpecialSavedItemFolder(selectedFolder)}
            rename={!isSpecialSavedItemFolder(selectedFolder)}
            enabled={!!selectedFolder}
            onAction={this.handleFolderAction}
          />
        </StickyHeader>
        <FolderContainer>
          <FolderCategory>
            <GlobalCategoryIcon>
              <FontAwesomeIcon fixedWidth icon={faServer} size="lg" />
            </GlobalCategoryIcon>
            {localize("globalFolder")}
          </FolderCategory>
          <Tree className="root">
            <Folder
              key="NeverExpiring"
              name={localize("neverExpiringFolder")}
              onSelected={this.props.selectFolder}
              selected={
                SpecialSavedItemFolderIds.GlobalNeverExpiring ===
                this.props.selectedFolder
              }
              id={SpecialSavedItemFolderIds.GlobalNeverExpiring}
            >
              {globalFolders
                .filter(f => !f.expirationHours && !f.parentFolderId)
                .map(f => this.renderFolder(globalFolders, f.id))}
            </Folder>
            <Folder
              key="7Days"
              name={localize("weekFolder")}
              onSelected={this.props.selectFolder}
              selected={
                SpecialSavedItemFolderIds.GlobalWeek ===
                this.props.selectedFolder
              }
              id={SpecialSavedItemFolderIds.GlobalWeek}
            >
              {globalFolders
                .filter(
                  f =>
                    f.expirationHours === SavedItemExpiration.WEEK &&
                    !f.parentFolderId
                )
                .map(f => this.renderFolder(globalFolders, f.id))}
            </Folder>
            <Folder
              key="24Hours"
              name={localize("dayFolder")}
              onSelected={this.props.selectFolder}
              selected={
                SpecialSavedItemFolderIds.GlobalDay ===
                this.props.selectedFolder
              }
              id={SpecialSavedItemFolderIds.GlobalDay}
            >
              {globalFolders
                .filter(
                  f =>
                    f.expirationHours === SavedItemExpiration.DAY &&
                    !f.parentFolderId
                )
                .map(f => this.renderFolder(globalFolders, f.id))}
            </Folder>
          </Tree>
          <FolderCategory>
            <UserCategoryIcon>
              <FontAwesomeIcon fixedWidth icon={faUser} size="lg" />
            </UserCategoryIcon>
            {localize("userFolder")}
          </FolderCategory>
          <Tree className="root">
            <Folder
              key="NeverExpiring"
              name={localize("neverExpiringFolder")}
              onSelected={this.props.selectFolder}
              selected={
                SpecialSavedItemFolderIds.UserNeverExpiring ===
                this.props.selectedFolder
              }
              id={SpecialSavedItemFolderIds.UserNeverExpiring}
            >
              {userFolders
                .filter(f => !f.expirationHours && !f.parentFolderId)
                .map(f => this.renderFolder(userFolders, f.id))}
            </Folder>
            <Folder
              key="7Days"
              name={localize("weekFolder")}
              onSelected={this.props.selectFolder}
              selected={
                SpecialSavedItemFolderIds.UserWeek === this.props.selectedFolder
              }
              id={SpecialSavedItemFolderIds.UserWeek}
            >
              {userFolders
                .filter(
                  f =>
                    f.expirationHours === SavedItemExpiration.WEEK &&
                    !f.parentFolderId
                )
                .map(f => this.renderFolder(userFolders, f.id))}
            </Folder>
            <Folder
              key="24Hours"
              name={localize("dayFolder")}
              onSelected={this.props.selectFolder}
              selected={
                SpecialSavedItemFolderIds.UserDay === this.props.selectedFolder
              }
              id={SpecialSavedItemFolderIds.UserDay}
            >
              {userFolders
                .filter(
                  f =>
                    f.expirationHours === SavedItemExpiration.DAY &&
                    !f.parentFolderId
                )
                .map(f => this.renderFolder(userFolders, f.id))}
            </Folder>
          </Tree>
        </FolderContainer>
      </FolderListContainer>
    );
  }
}

FolderTree.defaultProps = {
  folders: {},
  selectedFolder: null,
  pullFolders: false
};

FolderTree.propTypes = {
  folders: PropTypes.object,
  selectedFolder: PropTypes.number,
  selectFolder: PropTypes.func.isRequired,
  addFolder: PropTypes.func.isRequired,
  removeFolder: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  updateFolder: PropTypes.func.isRequired,
  getSavedItemFolders: PropTypes.func.isRequired,
  pullFolders: PropTypes.bool
};

const mapStateToProps = state => ({
  folders: state.savedItems.folders,
  selectedFolder: state.savedItems.selectedFolder,
  userId: state.auth.user.userId
});

const mapActionHandlers = {
  selectFolder: savedItemsActions.selectFolder,
  addFolder: savedItemsActions.addFolder,
  removeFolder: savedItemsActions.removeFolder,
  updateFolder: savedItemsActions.updateFolder,
  getSavedItemFolders: savedItemsActions.getSavedItemFolders
};

export default connect(mapStateToProps, mapActionHandlers)(FolderTree);



// WEBPACK FOOTER //
// ./app/components/SavedItems/FolderTree/FolderTree.js